import { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { REPORTS } from '@elementary/common-ui/constants/queryKeys';
import * as api from '../services/api';
import useEnvironments from './useEnvironments';

export type Report = { envId: string; isReportReady: boolean };

const useInitializingReportsPolling = () => {
  const { data: envs } = useEnvironments();

  const { mutateAsync: fetchReport } = useMutation((envId: string) =>
    api.fetchReport(envId),
  );

  const [readyReportsEnvIds, setReadyReportsEnvIds] = useState<string[]>([]);

  const fetchReportWithEnvId = useCallback(
    async (envId: string): Promise<Report> => {
      try {
        await fetchReport(envId);
        return { envId, isReportReady: true };
      } catch (error) {
        return { envId, isReportReady: false };
      }
    },
    [fetchReport],
  );

  const fetchReports = useCallback(async () => {
    if (envs) {
      const queries = envs
        .filter(
          (env) => !env.lastSyncedAt && !readyReportsEnvIds.includes(env.id),
        )
        .map((env) => fetchReportWithEnvId(env.id));

      const resolved = await Promise.all(queries);
      return resolved;
    }
    return [];
  }, [envs, fetchReportWithEnvId, readyReportsEnvIds]);

  const { refetch: refetchReports } = useQuery(
    [REPORTS],
    () => fetchReports(),
    {
      enabled: !!envs,
      refetchInterval: 15000,
      keepPreviousData: true,
      onSuccess: (data) => {
        const filteredReadyReports = data
          .filter((report) => report.isReportReady)
          .map((report) => report.envId);
        setReadyReportsEnvIds((prev) => [...prev, ...filteredReadyReports]);
      },
    },
  );

  useEffect(() => {
    refetchReports();
  }, [envs, refetchReports]);
};

export default useInitializingReportsPolling;
